import { useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";

import { t } from "../../i18n/t";

export type SeverityFilter = "all" | "BLOCK" | "WARN";
export type ResolvedFilter = "unresolved" | "all";
export type TabKey = "coa" | "vat" | "contacts" | "dimensions";

export const TABS: readonly TabKey[] = ["coa", "vat", "contacts", "dimensions"];

export interface ReviewFilterState {
  severity: SeverityFilter;
  resolved: ResolvedFilter;
  tab: TabKey;
}

/** The query `list_review_items` is called with (§A9.7.1); 06a's `useReviewItems` takes a flat record. */
export type ReviewQuery = Record<string, string | number | boolean>;

function isTab(value: string | null): value is TabKey {
  return value !== null && (TABS as readonly string[]).includes(value);
}

function isSeverity(value: string | null): value is SeverityFilter {
  return value === "BLOCK" || value === "WARN" || value === "all";
}

/**
 * §B4.1. The filter bar's state lives in the URL query string, so a link to a filtered view is shareable and a
 * reload does not silently widen the filter an operator is working under.
 */
export function useReviewFilters(): {
  filters: ReviewFilterState;
  setSeverity: (value: SeverityFilter) => void;
  setResolved: (value: ResolvedFilter) => void;
  setCombo: (severity: SeverityFilter, resolved: ResolvedFilter) => void;
  setTab: (value: TabKey) => void;
  openTab: (value: TabKey) => void;
  query: ReviewQuery;
} {
  const [params, setParams] = useSearchParams();
  const rawSeverity = params.get("severity");
  const severity: SeverityFilter = isSeverity(rawSeverity) ? rawSeverity : "all";
  const resolved: ResolvedFilter = params.get("resolved") === "all" ? "all" : "unresolved";
  const tabParam = params.get("tab");
  const tab: TabKey = isTab(tabParam) ? tabParam : "coa";

  const patch = useCallback(
    (key: string, value: string) => {
      const next = new URLSearchParams(params);
      next.set(key, value);
      setParams(next, { replace: true });
    },
    [params, setParams],
  );

  const setSeverity = useCallback((value: SeverityFilter) => patch("severity", value), [patch]);
  const setResolved = useCallback((value: ResolvedFilter) => patch("resolved", value), [patch]);
  // A chip sets BOTH params; two `patch` calls would each build from the pre-click params and the second
  // would clobber the first.
  const setCombo = useCallback(
    (severity: SeverityFilter, resolved: ResolvedFilter) => {
      const next = new URLSearchParams(params);
      next.set("severity", severity);
      next.set("resolved", resolved);
      setParams(next, { replace: true });
    },
    [params, setParams],
  );
  const setTab = useCallback((value: TabKey) => patch("tab", value), [patch]);
  // xmg-webux (defect 2): "Open the tab to choose" lands on an OPEN item, so it also widens a severity
  // chip that would hide it (a "Warnings" chip hides a BLOCK row) -- one URL write, never two that clobber.
  const openTab = useCallback(
    (value: TabKey) => {
      const next = new URLSearchParams(params);
      next.set("tab", value);
      next.set("severity", "all");
      next.set("resolved", "unresolved");
      setParams(next, { replace: true });
    },
    [params, setParams],
  );

  const query = useMemo<ReviewQuery>(
    () => ({
      ...(severity === "all" ? {} : { severity }),
      ...(resolved === "all" ? {} : { resolved: false }),
    }),
    [severity, resolved],
  );

  return { filters: { severity, resolved, tab }, setSeverity, setResolved, setCombo, setTab, openTab, query };
}

/**
 * The redesign's filter chips (Review.html): "Show: Unresolved only / Blocking / Warnings / All". Each chip is
 * a (severity, resolved) pair of the old two selects: Blocking = BLOCK + unresolved, Warnings = WARN +
 * unresolved, All = all + all. The state still lives in the URL query string (§B4.1) — same params, so a
 * shared or reloaded link widens nothing.
 */
const CHIPS = [
  { key: "unresolved", label: () => t("review.filter_unresolved"), severity: "all", resolved: "unresolved" },
  { key: "blocking", label: () => t("review.chip_blocking"), severity: "BLOCK", resolved: "unresolved" },
  { key: "warnings", label: () => t("review.chip_warnings"), severity: "WARN", resolved: "unresolved" },
  { key: "all", label: () => t("review.filter_all"), severity: "all", resolved: "all" },
] as const;

export function ReviewFilters(): JSX.Element {
  const { filters, setCombo } = useReviewFilters();
  return (
    <div className="chiprow" role="group" aria-label={t("review.filter_legend")}>
      <span className="eyebrow">{t("review.filter_show")}</span>
      {CHIPS.map((chip) => {
        const active = filters.severity === chip.severity && filters.resolved === chip.resolved;
        return (
          <button
            key={chip.key}
            type="button"
            className="chip"
            aria-pressed={active}
            onClick={() => setCombo(chip.severity, chip.resolved)}
          >
            {chip.label()}
          </button>
        );
      })}
    </div>
  );
}
